(function () {
  /*
    VFR Album Tilt + Reflection

    Product cards:
    - Home page cards
    - Staff picks
    - Collections
    - Recently viewed
    - Product recommendations / You may also like

    Product page:
    - Main selected product image only
    - Does NOT tilt thumbnail nav
    - Does NOT tilt the full gray gallery wrapper
    - Does NOT run in search dropdown
  */

  const CARD_MAX_ROTATION = 8;
  const GALLERY_MAX_ROTATION = 3.5;

  const SEARCH_EXCLUDE_SELECTOR = [
    '.search-bar',
    '.search-bar__results',
    '.search-bar__menu-wrapper',
    '.header__search-bar-wrapper',
    '.predictive-search',
    '[class*="predictive-search"]',
    '[class*="search-results"]',
    '[class*="search__results"]'
  ].join(',');

  const THUMBNAIL_EXCLUDE_SELECTOR = [
    '.product-gallery__thumbnail',
    '.product-gallery__thumbnail-list',
    '.product-gallery__carousel-nav',
    '.product-gallery__navigation',
    '.product-gallery__nav'
  ].join(',');

  const targetImageMap = new WeakMap();
  const transformTargetMap = new WeakMap();
  const maxRotationMap = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function isInSearchArea(element) {
    return !!(element && element.closest(SEARCH_EXCLUDE_SELECTOR));
  }

  function isThumbnailImage(image) {
    return !!(image && image.closest(THUMBNAIL_EXCLUDE_SELECTOR));
  }

  function getFirstImage(element) {
    if (!element) {
      return null;
    }

    if (element.tagName && element.tagName.toLowerCase() === 'img') {
      return element;
    }

    return element.querySelector('img');
  }

  function getMainGalleryWrapper(image) {
    if (!image || isThumbnailImage(image) || isInSearchArea(image)) {
      return null;
    }

    const isMainGalleryImage =
      image.classList.contains('product-gallery__image') ||
      image.hasAttribute('data-zoom');

    if (!isMainGalleryImage) {
      return null;
    }

    /*
      Important:
      Use the image's closest small wrapper or parent.
      Do NOT use .product-gallery because that tilts the entire gray viewer
      and moves thumbnails.
    */
    return (
      image.closest('.product-gallery__media') ||
      image.closest('.product-gallery__image-wrapper') ||
      image.closest('.product-gallery__media-wrapper') ||
      image.parentElement
    );
  }

  function addReflectionLayer(wrapper, galleryMode) {
    if (wrapper.querySelector('.vfr-cover-light')) {
      return;
    }

    wrapper.classList.add(galleryMode ? 'vfr-product-gallery-light-ready' : 'vfr-axis-light-ready');

    const light = document.createElement('span');
    light.className = 'vfr-cover-light';
    light.setAttribute('aria-hidden', 'true');

    wrapper.appendChild(light);
  }

  function positionReflectionOverImage(wrapper) {
    const image = targetImageMap.get(wrapper) || getFirstImage(wrapper);

    if (!image) {
      return null;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    if (!wrapperRect.width || !wrapperRect.height || !imageRect.width || !imageRect.height) {
      return null;
    }

    const left = imageRect.left - wrapperRect.left;
    const top = imageRect.top - wrapperRect.top;

    wrapper.style.setProperty('--vfr-light-left', left.toFixed(2) + 'px');
    wrapper.style.setProperty('--vfr-light-top', top.toFixed(2) + 'px');
    wrapper.style.setProperty('--vfr-light-width', imageRect.width.toFixed(2) + 'px');
    wrapper.style.setProperty('--vfr-light-height', imageRect.height.toFixed(2) + 'px');

    return imageRect;
  }

  function updateReflection(wrapper, event) {
    const imageRect = positionReflectionOverImage(wrapper);

    if (!imageRect) {
      return;
    }

    const mouseX = clamp((event.clientX - imageRect.left) / imageRect.width, 0, 1);
    const mouseY = clamp((event.clientY - imageRect.top) / imageRect.height, 0, 1);

    /*
      Mirrored reflection:
      cursor top-left = light bottom-right
      cursor top-right = light bottom-left
      cursor bottom-left = light top-right
      cursor bottom-right = light top-left
    */
    const lightX = 100 - (mouseX * 100);
    const lightY = 100 - (mouseY * 100);

    wrapper.style.setProperty('--vfr-light-x', lightX.toFixed(2) + '%');
    wrapper.style.setProperty('--vfr-light-y', lightY.toFixed(2) + '%');
  }

  function updateAxisTilt(wrapper, event) {
    const image = targetImageMap.get(wrapper) || getFirstImage(wrapper);
    const transformTarget = transformTargetMap.get(wrapper) || wrapper;
    const rect = image ? image.getBoundingClientRect() : wrapper.getBoundingClientRect();

    if (!rect.width || !rect.height) {
      return;
    }

    const maxRotation = maxRotationMap.get(wrapper) || CARD_MAX_ROTATION;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const xPercent = clamp(x / rect.width, 0, 1);
    const yPercent = clamp(y / rect.height, 0, 1);

    const rotateY = (xPercent - 0.5) * maxRotation * 2;
    const rotateX = (0.5 - yPercent) * maxRotation * 2;

    transformTarget.style.setProperty('--vfr-axis-rotate-x', rotateX.toFixed(2) + 'deg');
    transformTarget.style.setProperty('--vfr-axis-rotate-y', rotateY.toFixed(2) + 'deg');
  }

  function resetAxisTilt(wrapper, event) {
  const transformTarget = transformTargetMap.get(wrapper) || wrapper;

  transformTarget.classList.add('vfr-axis-returning');

  window.setTimeout(function () {
    transformTarget.classList.remove('vfr-axis-returning');
  }, 140);

  transformTarget.style.setProperty('--vfr-axis-rotate-x', '0deg');
  transformTarget.style.setProperty('--vfr-axis-rotate-y', '0deg');

  /*
    Do not remove the reflection variables.
    Removing them makes the light snap back to 50% / 50%.
    Instead, push the reflection off-screen in the same mirrored direction,
    then let opacity fade it out.
  */
  const image = targetImageMap.get(wrapper) || getFirstImage(wrapper);

  if (!image || !event) {
    return;
  }

  const rect = image.getBoundingClientRect();

  if (!rect.width || !rect.height) {
    return;
  }

  const mouseX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
  const mouseY = clamp((event.clientY - rect.top) / rect.height, 0, 1);

  const lightX = 100 - (mouseX * 100);
  const lightY = 100 - (mouseY * 100);

  const exitLightX = lightX < 50 ? -35 : 135;
  const exitLightY = lightY < 50 ? -35 : 135;

  wrapper.style.setProperty('--vfr-light-x', exitLightX + '%');
  wrapper.style.setProperty('--vfr-light-y', exitLightY + '%');
}

  function enableTilt(wrapper, image, options) {
    const settings = options || {};

    if (!wrapper || wrapper.dataset.vfrAlbumAxisTilt === 'true' || isInSearchArea(wrapper)) {
      return;
    }

    if (!image || isThumbnailImage(image) || isInSearchArea(image)) {
      return;
    }

    const rect = image.getBoundingClientRect();

    if (rect.width < 80 || rect.height < 80) {
      return;
    }

    wrapper.dataset.vfrAlbumAxisTilt = 'true';

    targetImageMap.set(wrapper, image);
    transformTargetMap.set(wrapper, settings.transformTarget || wrapper);
    maxRotationMap.set(wrapper, settings.maxRotation || CARD_MAX_ROTATION);

    if (settings.galleryMode) {
      image.classList.add('vfr-product-gallery-tilt-image');
    } else {
      wrapper.classList.add('vfr-album-axis-tilt');
    }

    addReflectionLayer(wrapper, !!settings.galleryMode);
    positionReflectionOverImage(wrapper);

    wrapper.addEventListener('pointermove', function (event) {
      updateAxisTilt(wrapper, event);
      updateReflection(wrapper, event);
    });

    wrapper.addEventListener('pointerenter', function () {
      positionReflectionOverImage(wrapper);
    });

    wrapper.addEventListener('pointerleave', function (event) {
  resetAxisTilt(wrapper, event);
});
  }

  function initProductCardTilts(root) {
    root.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
      if (isInSearchArea(link) || link.closest('.product-gallery')) {
        return;
      }

      const image = link.querySelector('img');

      if (!image || isThumbnailImage(image) || isInSearchArea(image)) {
        return;
      }

      enableTilt(link, image, {
        maxRotation: CARD_MAX_ROTATION,
        galleryMode: false,
        transformTarget: link
      });
    });
  }

  function initMainProductGalleryTilts(root) {
    root.querySelectorAll('.product-gallery__image, .product-gallery img[data-zoom]').forEach(function (image) {
      if (isThumbnailImage(image) || isInSearchArea(image)) {
        return;
      }

      const wrapper = getMainGalleryWrapper(image);

      if (!wrapper) {
        return;
      }

      enableTilt(wrapper, image, {
        maxRotation: GALLERY_MAX_ROTATION,
        galleryMode: true,
        transformTarget: image
      });
    });
  }

  function initAxisTilt(context) {
    const root = context || document;

    initProductCardTilts(root);
    initMainProductGalleryTilts(root);
  }

  function refreshReflectionPositions() {
    document.querySelectorAll('[data-vfr-album-axis-tilt="true"]').forEach(function (wrapper) {
      positionReflectionOverImage(wrapper);
    });
  }

  function scheduleInit(context) {
    window.requestAnimationFrame(function () {
      initAxisTilt(context || document);
      refreshReflectionPositions();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleInit(document);
    });
  } else {
    scheduleInit(document);
  }

  window.addEventListener('load', function () {
    scheduleInit(document);

    /*
      Product recommendations / "You may also like" often load late.
    */
    window.setTimeout(function () {
      scheduleInit(document);
    }, 700);

    window.setTimeout(function () {
      scheduleInit(document);
    }, 1600);
  });

  window.addEventListener('resize', function () {
    refreshReflectionPositions();
  });

  document.addEventListener('shopify:section:load', function (event) {
    scheduleInit(event.target);
  });

  document.addEventListener('variant:changed', function () {
    window.setTimeout(function () {
      scheduleInit(document);
    }, 150);
  });

  document.addEventListener('product:variant-change', function () {
    window.setTimeout(function () {
      scheduleInit(document);
    }, 150);
  });

  let observerTimer;

  const observer = new MutationObserver(function () {
    window.clearTimeout(observerTimer);

    observerTimer = window.setTimeout(function () {
      scheduleInit(document);
    }, 250);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();